/* Store page operational data: Step 4 authority cutover.
   All visible operational store data comes from normalized BB.stores.fetchOne(slug).
   regularHours is the regular 7-day schedule; today is the explicit effective projection for today's row.
   upcomingDays is never mined for inferred exceptions: only entries with an explicit public note are surfaced. */
(function () {
  'use strict';
  const BB = window.BB;
  const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  function weekdayOf(date) {
    if (typeof date !== 'string' || !date) return null;
    const parsed = new Date(date + 'T12:00:00');
    return Number.isNaN(parsed.getTime()) ? null : DAYS[parsed.getDay()];
  }

  function regularRows(store) {
    const rows = BB.stores.format.regularHours(store && store.regularHours);
    if (!rows) return null;
    const todayKey = store && store.today ? weekdayOf(store.today.date) : null;
    const today = todayKey ? BB.stores.format.today(store) : null;
    return rows.map((row) => {
      if (todayKey && row.weekday === todayKey && today) {
        return { weekday: row.weekday, state: today.state, text: today.text, isToday: true };
      }
      return { weekday: row.weekday, state: row.state, text: row.text, isToday: false };
    });
  }

  function upcomingRows(store) {
    if (!store || !Array.isArray(store.upcomingDays)) return [];
    return store.upcomingDays.filter((day) => day && day.note).map((day) => {
      const formatted = BB.stores.format.upcomingDay(day);
      return { date: day.date, weekday: day.weekday, state: formatted.state, text: formatted.text, note: formatted.note };
    });
  }

  function dateLabel(date) {
    if (typeof date !== 'string' || !date) return '';
    const parsed = new Date(date + 'T12:00:00');
    if (Number.isNaN(parsed.getTime())) return date;
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || 'nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).format(parsed).replace('.', '');
    } catch (e) { return date; }
  }

  const SCHEMA_DAYS = {
    monday: 'https://schema.org/Monday',
    tuesday: 'https://schema.org/Tuesday',
    wednesday: 'https://schema.org/Wednesday',
    thursday: 'https://schema.org/Thursday',
    friday: 'https://schema.org/Friday',
    saturday: 'https://schema.org/Saturday',
    sunday: 'https://schema.org/Sunday'
  };

  function absoluteHttpUrl(value) {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
    } catch (e) { return null; }
  }

  function openingHoursSpecifications(store) {
    if (!store || store.regularHours === null || !Array.isArray(store.regularHours)) return null;
    const specs = [];
    for (const row of store.regularHours) {
      const dayOfWeek = row && SCHEMA_DAYS[row.weekday];
      if (!dayOfWeek || !Array.isArray(row.windows)) return null;
      for (const window of row.windows) {
        if (!window || typeof window.openTime !== 'string' || typeof window.closeTime !== 'string') return null;
        specs.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: dayOfWeek,
          opens: window.openTime,
          closes: window.closeTime
        });
      }
    }
    return specs;
  }

  function structuredDataObject(store, config) {
    if (!store || !store.name) return null;
    const data = { '@context': 'https://schema.org', '@type': 'FurnitureStore', name: store.name };
    const url = absoluteHttpUrl(store.shopifyPageUrl) || absoluteHttpUrl(config && config.canonicalUrl);
    const image = absoluteHttpUrl(config && config.imageUrl);
    if (url) data.url = url;
    if (image) data.image = image;
    if (store.contact && store.contact.phone) data.telephone = store.contact.phone;
    if (store.contact && store.contact.email) data.email = store.contact.email;

    if (store.address) {
      const address = { '@type': 'PostalAddress' };
      const formatted = BB.stores.format.address(store.address);
      if (formatted.line1) address.streetAddress = formatted.line1;
      if (store.address.postalCode) address.postalCode = store.address.postalCode;
      if (store.address.city) address.addressLocality = store.address.city;
      if (store.address.countryCode) address.addressCountry = store.address.countryCode;
      if (Object.keys(address).length > 1) data.address = address;
    }

    if (store.coordinates) {
      data.geo = {
        '@type': 'GeoCoordinates',
        latitude: store.coordinates.latitude,
        longitude: store.coordinates.longitude
      };
    }

    const openingHours = openingHoursSpecifications(store);
    if (openingHours && openingHours.length) data.openingHoursSpecification = openingHours;

    if (config && config.parentName) data.parentOrganization = { '@type': 'Organization', name: config.parentName };
    return data;
  }

  BB.storeHours = {
    weekdayOf: weekdayOf,
    regularRows: regularRows,
    upcomingRows: upcomingRows,
    dateLabel: dateLabel,
    openingHoursSpecifications: openingHoursSpecifications,
    structuredDataObject: structuredDataObject
  };

  const root = document.querySelector('[data-bb-store-view]');
  if (!root) return;
  const slug = root.getAttribute('data-store-slug');
  const structuredDataConfig = {
    enabled: root.getAttribute('data-store-schema-enabled') === 'true',
    canonicalUrl: root.getAttribute('data-store-schema-canonical-url') || null,
    imageUrl: root.getAttribute('data-store-schema-image-url') || null,
    parentName: root.getAttribute('data-store-schema-parent-name') || null
  };
  BB.track('store_view', { store_slug: slug }, slug);

  function removeStructuredData() {
    document.querySelectorAll('script[data-bb-store-jsonld]').forEach((script) => script.remove());
  }

  function renderStructuredData(store) {
    removeStructuredData();
    if (!structuredDataConfig.enabled) return;
    const data = structuredDataObject(store, structuredDataConfig);
    if (!data) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-bb-store-jsonld', '');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }

  function setLink(link, href) {
    if (!link) return;
    if (href) { link.setAttribute('href', href); link.hidden = false; }
    else { link.removeAttribute('href'); link.hidden = true; }
  }


  /* Only effective 14-day Store Platform dates. Never infer the next opening from regularHours. */
  function nextOpening(store) {
    if (!store || store.openNow !== false || !store.today || !store.timezone || !Array.isArray(store.upcomingDays)) return null;
    let parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', { timeZone: store.timezone, year: 'numeric',
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
    } catch (e) { return null; }
    const value = (key) => { const p = parts.find((part) => part.type === key); return p && p.value; };
    const today = [value('year'), value('month'), value('day')].join('-');
    const time = [value('hour'), value('minute')].join(':');
    if (store.today.date !== today) return null;
    const dayNumber = (date) => /^\d{4}-\d{2}-\d{2}$/.test(date || '') ? Date.parse(date + 'T00:00:00Z') / 86400000 : NaN;
    const todayNumber = dayNumber(today);
    if (!Number.isFinite(todayNumber)) return null;
    let previous = todayNumber - 1;
    const days = store.upcomingDays.slice().sort((a, b) => String(a.date).localeCompare(String(b.date)));
    for (const day of days) {
      if (!day || !day.date) return null;
      const number = dayNumber(day.date);
      if (!Number.isFinite(number)) return null;
      if (number < todayNumber) continue;
      if (number !== previous + 1 || day.closed === null || !Array.isArray(day.windows)) return null;
      previous = number;
      if (day.closed) continue;
      if (day.windows.length === 0) return null;
      const windows = day.windows.slice().sort((a, b) => a.openTime.localeCompare(b.openTime));
      for (const window of windows) {
        if (!/^\d{2}:\d{2}$/.test(window.openTime)) return null;
        if (number === todayNumber && window.openTime <= time) continue;
        if (number === todayNumber) return BB.t('storeNextOpenToday', { time: window.openTime });
        if (number === todayNumber + 1) return BB.t('storeNextOpenTomorrow', { time: window.openTime });
        let dateLabel;
        try { dateLabel = new Intl.DateTimeFormat(document.documentElement.lang || 'nl-NL',
          { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
          .format(new Date(day.date + 'T12:00:00Z')); }
        catch (e) { return null; }
        return BB.t('storeNextOpenDate', { day: dateLabel, time: window.openTime });
      }
    }
    return null;
  }

  function setStatus(state, text) {
    const el = root.querySelector('[data-store-status]');
    if (!el) return;
    el.classList.remove('is-open', 'is-closed');
    el.textContent = text || '';
    if (state === 'open' || state === 'closed') el.classList.add('is-' + state);
  }

  function clearHours() {
    const list = root.querySelector('[data-store-regular-hours]');
    if (list) {
      list.hidden = true;
      list.querySelectorAll('[data-hours-value]').forEach((dd) => { dd.textContent = ''; });
      list.querySelectorAll('.bb-store__hours-row').forEach((row) => row.classList.remove('is-today', 'is-override'));
    }
    const special = root.querySelector('[data-store-special-hours]');
    const specialList = root.querySelector('[data-store-special-hours-list]');
    if (specialList) specialList.innerHTML = '';
    if (special) special.hidden = true;
  }

  function clearOperationalLinks() {
    root.querySelectorAll('[data-bb-store-route], [data-bb-store-call], [data-store-email]').forEach((link) => setLink(link, null));
    const heroPhone = root.querySelector('[data-store-hero-phone]');
    if (heroPhone) heroPhone.hidden = true;
  }

  function applyDegraded(state) {
    root.setAttribute('data-store-authority-state', state);
    root.setAttribute('data-store-content', 'missing');
    root.removeAttribute('aria-busy');
    const result = BB.stores.format.resultState({ state: state });
    const heroAddress = root.querySelector('[data-store-hero-address]');
    const address = root.querySelector('[data-store-address]');
    const dataState = root.querySelector('[data-store-data-state]');
    const hoursState = root.querySelector('[data-store-hours-state]');
    const map = root.querySelector('[data-store-map]');
    if (heroAddress) heroAddress.hidden = true;
    if (address) { address.textContent = ''; address.hidden = true; }
    if (dataState) { dataState.textContent = result.text; dataState.hidden = false; }
    if (hoursState) { hoursState.textContent = result.text; hoursState.hidden = false; }
    if (map) map.hidden = true;
    clearOperationalLinks();
    clearHours();
    removeStructuredData();
    setStatus('unknown', result.text);
  }

  function renderRegularHours(store) {
    const state = root.querySelector('[data-store-hours-state]');
    const list = root.querySelector('[data-store-regular-hours]');
    const rows = regularRows(store);
    if (!rows || !list) {
      if (state) { state.textContent = BB.t('storeHoursUnavailable'); state.hidden = false; }
      if (list) list.hidden = true;
      return;
    }
    const byWeekday = new Map(rows.map((row) => [row.weekday, row]));
    list.querySelectorAll('.bb-store__hours-row').forEach((el) => {
      const row = byWeekday.get(el.getAttribute('data-weekday'));
      const dd = el.querySelector('[data-hours-value]');
      el.classList.toggle('is-today', !!(row && row.isToday));
      if (dd) dd.textContent = row ? row.text : BB.t('storeHoursUnavailable');
    });
    if (state) state.hidden = true;
    list.hidden = false;
  }

  function renderUpcoming(store) {
    const box = root.querySelector('[data-store-special-hours]');
    const list = root.querySelector('[data-store-special-hours-list]');
    if (!box || !list) return;
    list.innerHTML = '';
    const rows = upcomingRows(store);
    rows.forEach((row) => {
      const wrap = document.createElement('div');
      wrap.className = 'bb-store__hours-row';
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      const value = document.createElement('span');
      const note = document.createElement('span');
      dt.textContent = dateLabel(row.date);
      value.textContent = row.text;
      note.className = 'bb-small bb-muted';
      note.textContent = row.note;
      dd.appendChild(value);
      dd.appendChild(document.createElement('br'));
      dd.appendChild(note);
      wrap.appendChild(dt);
      wrap.appendChild(dd);
      list.appendChild(wrap);
    });
    box.hidden = rows.length === 0;
  }

  function applyIdentityContext(store) {
    const name = store && store.name;
    const city = store && store.address && store.address.city;

    const title = root.querySelector('[data-store-operational-name]');
    if (title && name) title.textContent = name;

    root.querySelectorAll('[data-bb-appointment]').forEach((el) => {
      if (name) el.setAttribute('data-store-name', name);
      else el.removeAttribute('data-store-name');
    });

    const reviewTitle = root.querySelector('[data-store-review-title]');
    if (reviewTitle) {
      const fallback = reviewTitle.getAttribute('data-store-review-title-fallback') || reviewTitle.textContent;
      const template = reviewTitle.getAttribute('data-store-review-title-template');
      reviewTitle.textContent = name && template ? template.replace('[store]', name) : fallback;
    }

    const eyebrow = root.querySelector('[data-store-appointment-eyebrow]');
    if (eyebrow) {
      const fallback = eyebrow.getAttribute('data-store-appointment-eyebrow-fallback') || eyebrow.textContent;
      const template = eyebrow.getAttribute('data-store-appointment-eyebrow-template');
      eyebrow.textContent = city && template ? template.replace('[city]', city) : fallback;
    }

    const heroImage = root.querySelector('[data-store-hero-media] img');
    if (heroImage && name) heroImage.alt = name;
  }

  function applyPlatform(store, result) {
    root.setAttribute('data-store-authority-state', BB.stores.states.PLATFORM);
    root.setAttribute('data-store-content', (result && result.contentState) || store.projectionState || 'missing');
    root.removeAttribute('aria-busy');

    applyIdentityContext(store);

    const address = BB.stores.format.address(store.address);
    const heroAddress = root.querySelector('[data-store-hero-address]');
    const heroAddressText = root.querySelector('[data-store-hero-address] [data-store-address-text]');
    const addressEl = root.querySelector('[data-store-address]');
    if (heroAddressText) heroAddressText.textContent = address.singleLine || '';
    if (heroAddress) heroAddress.hidden = !address.singleLine;
    if (addressEl) { addressEl.textContent = address.singleLine || ''; addressEl.hidden = !address.singleLine; }

    const dataState = root.querySelector('[data-store-data-state]');
    if (dataState) dataState.hidden = true;

    const open = BB.stores.format.openState(store);
    const next = nextOpening(store);
    setStatus(open.state, next ? open.text + ' · ' + next : open.text);

    const route = BB.stores.format.routeUrl(store);
    root.querySelectorAll('[data-bb-store-route]').forEach((link) => setLink(link, route));

    const phone = store.contact && store.contact.phone;
    const phoneHref = BB.stores.format.telHref(store);
    root.querySelectorAll('[data-bb-store-call]').forEach((link) => {
      setLink(link, phoneHref);
      const text = link.querySelector('[data-store-phone-text]');
      if (text) text.textContent = phoneHref && phone ? phone : '';
      const bookingText = link.querySelector('[data-store-booking-phone-text]');
      if (bookingText) {
        const template = link.getAttribute('data-call-template') || '[phone]';
        bookingText.textContent = phoneHref && phone ? template.replace('[phone]', phone) : '';
      }
    });
    const heroPhone = root.querySelector('[data-store-hero-phone]');
    if (heroPhone) heroPhone.hidden = !phoneHref;

    const email = store.contact && store.contact.email;
    const emailHref = BB.stores.format.emailHref(store);
    root.querySelectorAll('[data-store-email]').forEach((link) => {
      setLink(link, emailHref);
      const text = link.querySelector('[data-store-email-text]');
      if (text) text.textContent = emailHref && email ? email : '';
    });

    const map = root.querySelector('[data-store-map]');
    if (map) {
      if (store.coordinates && typeof map.setStores === 'function') {
        map.hidden = false;
        map.setStores([{ slug: store.slug, name: store.name || store.slug, lat: store.coordinates.latitude, lng: store.coordinates.longitude }]);
        if (typeof map.select === 'function') map.select(store.slug, false);
      } else map.hidden = true;
    }

    renderRegularHours(store);
    renderUpcoming(store);
    renderStructuredData(store);
  }

  if (!slug) { applyDegraded(BB.stores.states.UNLISTED); return; }

  BB.stores.fetchOne(slug).then((result) => {
    if (result && result.state === BB.stores.states.PLATFORM && result.store) applyPlatform(result.store, result);
    else applyDegraded(result && result.state === BB.stores.states.UNLISTED ? BB.stores.states.UNLISTED : BB.stores.states.UNAVAILABLE);
  }).catch(() => applyDegraded(BB.stores.states.UNAVAILABLE));
})();
